"use client";

import { useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { FiBell } from "react-icons/fi";

import CdnImage from "@/components/CdnImage";
import { useT } from "@/hooks/useT";
import { getCountryFlagUrl } from "@/lib/country-data";
import { useTradingModalStore } from "@/stores/useTradingModalStore";
import TradingPanel from "@/app/market/[slug]/components/TradingPanel";
import { MarketPriceProvider } from "@/app/market/[slug]/context/MarketPriceContext";
import OpenInterestChart from "@/app/market/[slug]/components/OpenInterestChart";
import OpenOrders from "@/app/market/[slug]/components/OpenOrders";
import SourceAwareOrderBook from "@/app/market/[slug]/components/SourceAwareOrderBook";
import SourceAwareTrades from "@/app/market/[slug]/components/SourceAwareTrades";
import KalshiRelatedSeries from "./KalshiRelatedSeries";
import MyPositions from "@/app/market/[slug]/components/MyPositions";
import {
  SpotifyContextSidebar,
  PoliticsAISidebar,
  FinanceAISidebar,
  GeopoliticsAISidebar,
  CryptoAISidebar,
  AwardsAISidebar,
  WeatherAISidebar,
  SportsContextSidebar,
  MatchContextSidebar,
  GamingContextSidebar,
  XTrackerContextSidebar,
  XTrackerProgressChart,
  XTrackerVelocityGauge,
  AIContextSidebar,
  GenericEventSidebar,
} from "@/app/components/market-widgets";
import CollapsibleSection from "@/app/components/CollapsibleSection";
import { TrendingUpDownIcon } from "@/app/components/icons/TrendingUpDownIcon";
import { ChartColumnIcon } from "@/app/components/icons/ChartColumnIcon";
import MarketCohortBreakdown from "./MarketCohortBreakdown";
import RelatedEventsByTags from "./RelatedEventsByTags";
import type { EventData } from "@/hooks/useEventQuery";
import type {
  Market,
  Source,
  OpenTradingModalFn,
  GetLivePriceFn,
  GetLiveBuyPricesFn,
} from "./types";
import { formatPriceCents } from "./types";

const TopHoldersTable = dynamic(
  () => import("@/app/components/TopHoldersTable"),
  { ssr: false }
);

const SmartMoneyIndicator = dynamic(
  () => import("@/app/market/[slug]/components/SmartMoneyIndicator"),
  { ssr: false }
);

interface EventSidebarProps {
  selectedMarket: Market;
  source: Source;
  isKalshi: boolean;
  eventData: EventData;
  activeMarkets: Market[];
  // Sidebar state
  sidebarOpen: boolean;
  setSidebarOpen: (v: boolean) => void;
  alertModalOpen: boolean;
  setAlertModalOpen: (v: boolean) => void;
  // Context
  hasContextSidebar: boolean;
  widgetConfig: any;
  shouldUseAI: boolean;
  aiAnalysis: any;
  aiLoading: boolean;
  // Trading
  getLivePrice: GetLivePriceFn;
  getLiveBuyPrices: GetLiveBuyPricesFn;
  openTradingModal: OpenTradingModalFn;
  getRealtimePrice: GetLivePriceFn;
  getBuyPrices: GetLiveBuyPricesFn;
  onSelectMarket?: (marketId: string) => void;
  // Initial outcome to preselect (from query param, e.g. ?outcome=yes)
  initialOutcomeIndex?: 0 | 1 | null;
  // Initial trade mode (from query param ?action=sell). Lets the
  // portfolio "sell" link land directly in the SELL panel with the
  // user's position seeded for the "You own X @ Y" line.
  initialMode?: "buy" | "sell";
  seedOwnedPosition?: { outcome: "yes" | "no"; shares: number; avgPrice: number; currentPrice?: number } | null;
  /** Optional override for the MyPositions Sell button. When provided
   *  we route the click into the parent's TradingPanel (driving
   *  initialMode + seedOwnedPosition state) instead of opening a
   *  per-source modal. Keeps the user's "siempre el panel" UX on
   *  the event page where the panel is already mounted. */
  onRequestSell?: (position: any) => void;
}

export function EventSidebar({
  selectedMarket,
  source,
  isKalshi,
  eventData,
  activeMarkets,
  sidebarOpen,
  setSidebarOpen,
  alertModalOpen,
  setAlertModalOpen,
  hasContextSidebar,
  widgetConfig,
  shouldUseAI,
  aiAnalysis,
  aiLoading,
  getLivePrice,
  getLiveBuyPrices,
  openTradingModal,
  getRealtimePrice,
  getBuyPrices,
  onSelectMarket,
  initialOutcomeIndex,
  initialMode,
  seedOwnedPosition,
  onRequestSell,
}: EventSidebarProps) {
  const t = useT();
  const [marketDropdownOpen, setMarketDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { openPolymarket, openPredict } = useTradingModalStore();

  // Ocultar "View AI Context" (botón + panel) en partidos de Predict (sports
  // moneyline, ej. fifwc): el contexto IA no aplica a un 1X2 y mostraba
  // "Match data unavailable". Los futures (Winner) NO son moneyline → lo conservan.
  const isPredictSportsMatch = source === 'predict' &&
    (eventData?.markets || []).some((m: any) => m.sportsMarketType === 'moneyline');

  // Ocultar "View AI Context" (botón + panel) en TODO Predict: el análisis IA
  // se nutre de fuentes Polymarket/Kalshi y muestra "data unavailable" para
  // mercados de Predict. (Antes solo se ocultaba en partidos sports.)
  const hideAIContext = source === 'predict';

  // Event-scope lists so MyPositions / OpenOrders show the user's entire
  // exposure across every sub-market of this event, not just the one they
  // happen to have selected. Also kills the flicker caused by the
  // per-market re-fetch that fired on every sub-market switch.
  const eventConditionIds = (activeMarkets || [])
    .map((m) => m.conditionId)
    .filter((c): c is string => !!c);
  const eventMarketSlugs = (activeMarkets || [])
    .map((m) => m.slug)
    .filter((s): s is string => !!s);
  const eventTokenIds = (activeMarkets || [])
    .flatMap((m) => (m.clobTokenIds as string[] | undefined) || [])
    .filter((t): t is string => !!t);
  // Metadata lookups so each position / order row can show which
  // sub-market it belongs to (image + short label). Short label prefers
  // `groupItemTitle` (Polymarket's per-sub-market display name, e.g.
  // "Spider-Man: Brand New Day") and falls back to the full question.
  const marketMetaByConditionId: Record<string, { image?: string | null; label: string }> = {};
  const marketMetaByTokenId: Record<string, { image?: string | null; label: string; outcomes: string[] }> = {};
  for (const m of activeMarkets || []) {
    const label = m.groupItemTitle || m.question || m.slug || '';
    if (m.conditionId) {
      marketMetaByConditionId[m.conditionId.toLowerCase()] = {
        image: (m as any).image || eventData?.image || null,
        label,
      };
    }
    for (const tid of (m.clobTokenIds as string[] | undefined) || []) {
      if (!tid) continue;
      marketMetaByTokenId[tid.toLowerCase()] = {
        image: (m as any).image || eventData?.image || null,
        label,
        outcomes: m.outcomes || ['Yes', 'No'],
      };
    }
  }
  // En partidos de Predict (sports moneyline) el outcome seleccionado es un
  // equipo → priorizar su bandera sobre la imagen del evento, igual que la
  // grid de outcomes. getCountryFlagUrl → null para no-países (sin falsos).
  const selectedFlag = isPredictSportsMatch
    ? getCountryFlagUrl(selectedMarket.groupItemTitle || selectedMarket.question)
    : null;
  const selectedMarketImage = selectedFlag || selectedMarket.imageUrl || selectedMarket.image || eventData?.image || undefined;
  const kalshiTicker = isKalshi
    ? String((selectedMarket as any).ticker || selectedMarket.slug || selectedMarket.conditionId || selectedMarket.id || '').replace(/^ks-/i, '').toUpperCase()
    : undefined;
  const hideSelectedMarketHeader = source === "polymarket" && (
    selectedMarket.slug?.includes("updown")
    || (selectedMarket.question || "").toLowerCase().includes("up or down")
  );

  // Sell click from MyPositions. Two modes:
  //   - Parent provided onRequestSell (the event page does this) →
  //     drive the right-sidebar TradingPanel into SELL mode with the
  //     position seeded. No modal, no URL change.
  //   - Otherwise fall back to opening the per-source TradingModal so
  //     legacy mounts (if any future caller forgets to wire the
  //     callback) keep working.
  const handleSellClick = (position: any) => {
    if (onRequestSell) {
      onRequestSell(position);
      return;
    }
    // Find the correct outcome index by matching position.outcome against market outcomes
    const outcomes = selectedMarket.outcomes || ["Yes", "No"];
    const posOutcome = (position.outcome || "").toLowerCase();
    // Match by name to determine the real index in this market's outcomes
    const realIndex = outcomes.findIndex((o: string) => o.toLowerCase() === posOutcome);
    const outcomeIndex = realIndex >= 0 ? realIndex : (position.outcomeIndex ?? 0);
    const outcome = outcomeIndex === 0 ? "YES" : "NO";
    const [liveYes] = getLivePrice(selectedMarket);
    const price = outcomeIndex === 0 ? (liveYes ?? 0.5) : (1 - (liveYes ?? 0.5));
    // Real labels for both sides — derive from the API outcomes so the
    // modal renders Over/Under, Up/Down, sports team names.
    const sideLabel = outcomes[outcomeIndex];
    const altSideLabel = outcomes[outcomeIndex === 0 ? 1 : 0];

    if (source === 'predict') {
      openPredict({
        marketId: selectedMarket.id,
        marketSlug: selectedMarket.slug || '',
        outcome: outcome as "YES" | "NO",
        outcomePrice: price,
        marketQuestion: selectedMarket.question,
        marketImage: selectedMarket.image,
        altOutcomePrice: 1 - price,
        outcomes,
        actualOutcome: sideLabel,
        altActualOutcome: altSideLabel,
        mode: "sell",
        positionShares: position.shares,
        positionAvgPrice: position.avgPrice,
        positionAmountRaw: position.amountRaw,
      });
    } else if (source === 'kalshi') {
      const { openKalshi } = useTradingModalStore.getState();
      openKalshi({
        marketTicker: kalshiTicker || selectedMarket.slug || selectedMarket.conditionId,
        dflowTicker: kalshiTicker,
        marketTitle: selectedMarket.question,
        outcome: outcome.toLowerCase() as "yes" | "no",
        currentPrice: price,
        altPrice: 1 - price,
        yesMint: (selectedMarket as any).yesMint,
        noMint: (selectedMarket as any).noMint,
        mode: "sell",
        positionSize: position.shares,
        avgEntryPrice: position.avgPrice,
      });
    } else {
      openPolymarket({
        marketSlug: selectedMarket.slug,
        outcome: outcome as "YES" | "NO",
        outcomePrice: price,
        marketQuestion: selectedMarket.question,
        marketImage: selectedMarket.image,
        altOutcomePrice: 1 - price,
        outcomes,
        actualOutcome: sideLabel,
        altActualOutcome: altSideLabel,
        clobTokenIds: selectedMarket.clobTokenIds || [],
        conditionId: selectedMarket.conditionId,
        mode: "sell",
        positionShares: position.shares,
        positionAvgPrice: position.avgPrice,
      });
    }
  };

  // Close dropdown on outside click
  useEffect(() => {
    if (!marketDropdownOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setMarketDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [marketDropdownOpen]);
  // Build the market context row — rendered INSIDE TradingPanel via the
  // `contextHeader` prop so the selected sub-market (image + title +
  // dropdown + alert + AI) sits as part of the Place Trade card instead
  // of a separate floating card on top. Null on closed events / crypto
  // updown — TradingPanel falls back to its own header.
  const marketContextNode = (!eventData?.closed && !hideSelectedMarketHeader && selectedMarket) ? (
    <div ref={dropdownRef} className="relative">
      <div className="flex items-center gap-2">
        <button
          onClick={() => activeMarkets.length > 1 && setMarketDropdownOpen(!marketDropdownOpen)}
          className={`flex items-center gap-2 min-w-0 flex-1 ${activeMarkets.length > 1 ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f0f12] rounded-[5px] px-1.5 py-1 -mx-1.5 -my-1 transition-colors' : ''}`}
        >
          {selectedMarketImage && (
            <CdnImage
              src={selectedMarketImage}
              fallbackSrc={eventData?.image || undefined}
              alt=""
              width={28}
              height={28}
              className="w-7 h-7 rounded-[5px] object-cover flex-shrink-0"
            />
          )}
          <span className="font-inter-tight font-bold text-sm text-gray-900 dark:text-white truncate">
            {selectedMarket.groupItemTitle || selectedMarket.question}
          </span>
          {activeMarkets.length > 1 && (
            <svg className={`w-3.5 h-3.5 text-gray-400 flex-shrink-0 transition-transform ${marketDropdownOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          )}
        </button>
        {/* Alert + AI — tenue pills (10% tint + colored icon/text), so the
            secondary actions don't compete with the trade CTA or the data.
            Each keeps its hue (amber/indigo) only as a subtle accent. */}
        <button
          onClick={() => setAlertModalOpen(true)}
          className="flex-shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[3px] bg-amber-500/10 dark:bg-amber-500/[0.12] text-amber-600 dark:text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-colors group"
          title={t("eventSidebar.setPriceAlert")}
        >
          <FiBell className="w-3.5 h-3.5 group-hover:animate-bell-ring" strokeWidth={2.4} />
          <span className="text-[11px] font-inter-tight font-semibold">{t("eventSidebar.alert")}</span>
        </button>
        {hasContextSidebar && !hideAIContext && widgetConfig && widgetConfig.widgetType !== "none" && (
          !(widgetConfig.widgetType === "match" && !widgetConfig.metadata?.matchInfo) &&
          <button
            onClick={() => setSidebarOpen(true)}
            className="flex-shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[3px] bg-indigo-500/10 dark:bg-indigo-500/[0.12] text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 hover:bg-indigo-500/20 transition-colors"
            title={t("eventSidebar.viewAIContext")}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M12 2v4"/>
              <circle cx="9" cy="16" r="1"/>
              <circle cx="15" cy="16" r="1"/>
            </svg>
            <span className="text-[11px] font-inter-tight font-semibold">{t("eventSidebar.viewAIContext")}</span>
          </button>
        )}
      </div>
      {marketDropdownOpen && activeMarkets.filter((m) => m.id !== selectedMarket.id).length > 0 && (
        <div className="absolute left-0 right-0 top-full mt-1 z-50 bg-white dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] shadow-lg max-h-[300px] overflow-y-auto">
          {activeMarkets.filter((m) => m.id !== selectedMarket.id).map((m) => {
            const [liveYes] = getLivePrice(m);
            const yesPrice = liveYes ?? m.outcomePrices?.[0] ?? 0.5;
            return (
              <button
                key={m.id}
                onClick={() => {
                  if (onSelectMarket) onSelectMarket(m.id);
                  setMarketDropdownOpen(false);
                }}
                className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-[#0f0f12]"
              >
                {(m.imageUrl || m.image || eventData?.image) && (
                  <CdnImage
                    src={m.imageUrl || m.image || eventData.image || ""}
                    fallbackSrc={eventData?.image || undefined}
                    alt=""
                    width={24}
                    height={24}
                    className="w-6 h-6 rounded-[5px] object-cover flex-shrink-0"
                  />
                )}
                <span className="font-inter-tight text-xs truncate flex-1 font-medium text-gray-700 dark:text-gray-300">
                  {m.groupItemTitle || m.question}
                </span>
                <span className="font-mono text-xs font-semibold text-gray-500 dark:text-gray-400 flex-shrink-0">
                  {formatPriceCents(yesPrice, m.decimalPrecision)}%
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  ) : null;

  return (
    <>
      {/* Right Sidebar — Trading Panel (sticky) */}
      {selectedMarket && (
        <div className="hidden lg:block min-w-0">
          <div className="sticky top-20 space-y-3 max-h-[calc(100dvh-5.5rem)] overflow-y-auto pb-4 scrollbar-hide">
            <div>
              {source === 'polymarket' ? (
                <MarketPriceProvider
                  clobTokenIds={selectedMarket.clobTokenIds || []}
                  initialPrices={(() => {
                    // Use live buy prices from the central hook (same source as outcome grid)
                    const { buyYes, buyNo } = getLiveBuyPrices(selectedMarket);
                    return [buyYes, buyNo];
                  })()}
                  realtimeUpdates
                >
                  <TradingPanel
                    marketData={{
                      slug: selectedMarket.slug,
                      question: selectedMarket.question,
                      outcomePrices: selectedMarket.outcomePrices,
                      outcomes: selectedMarket.outcomes,
                      clobTokenIds: selectedMarket.clobTokenIds,
                      image: selectedMarket.image,
                      groupItemTitle: selectedMarket.groupItemTitle,
                    }}
                    isActive={selectedMarket.active && selectedMarket.acceptingOrders && !selectedMarket.closed}
                    conditionId={selectedMarket.conditionId}
                    eventSlug={eventData.slug}
                    eventTitle={eventData.title}
                    initialOutcomeIndex={initialOutcomeIndex}
                    initialMode={initialMode}
                    seedOwnedPosition={seedOwnedPosition}
                    contextHeader={marketContextNode}
                  />
                </MarketPriceProvider>
              ) : (
                <TradingPanel
                  marketData={{
                    slug: selectedMarket.slug,
                    question: selectedMarket.question,
                    outcomePrices: (() => {
                      // Use live WS prices (same source as outcome grid buttons)
                      const { buyYes, buyNo } = getLiveBuyPrices(selectedMarket);
                      return [buyYes, buyNo];
                    })(),
                    outcomes: selectedMarket.outcomes,
                    clobTokenIds: selectedMarket.clobTokenIds,
                    image: selectedMarket.image,
                    decimalPrecision: selectedMarket.decimalPrecision,
                    groupItemTitle: selectedMarket.groupItemTitle,
                  }}
                  isActive={selectedMarket.active && selectedMarket.acceptingOrders && !selectedMarket.closed}
                  source={source}
                  kalshiTicker={kalshiTicker}
                  eventSlug={eventData.slug}
                  eventTitle={eventData.title}
                  initialOutcomeIndex={initialOutcomeIndex}
                  initialMode={initialMode}
                  seedOwnedPosition={seedOwnedPosition}
                  contextHeader={marketContextNode}
                />
              )}
            </div>


            {/* My Positions — event-scope so the user sees every sub-market
                they hold at once. Selected market drives the sell-click
                modal defaults but the list itself is event-wide.
                Hyperliquid is not supported here yet: MyPositions has no
                'hyperliquid' branch in buildPositionUrl, falls back to the
                Polymarket /api/portfolio/positions request with an empty
                conditionId, the response shape mismatches the renderer's
                expectations, and the page enters a #300 re-render loop.
                Render nothing for HL until proper HL support lands. */}
            {source !== 'hyperliquid' && (
              <MyPositions
                conditionId={selectedMarket.conditionId}
                acceptingOrders={selectedMarket.acceptingOrders}
                marketQuestion={selectedMarket.question}
                marketImage={selectedMarket.image || undefined}
                outcomes={selectedMarket.outcomes}
                source={source}
                kalshiTicker={kalshiTicker}
                predictMarketId={source === 'predict' ? selectedMarket.id : undefined}
                onSellClick={handleSellClick}
                allowedConditionIds={source === 'polymarket' ? eventConditionIds : undefined}
                allowedMarketSlugs={source !== 'polymarket' ? eventMarketSlugs : undefined}
                marketMetaByConditionId={marketMetaByConditionId}
              />
            )}

            {/* Open Orders — Polymarket only. Event-scope via allowedTokenIds
                so orders from any sub-market appear while scrolling. */}
            {!eventData?.closed && source === 'polymarket' && (
              <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] p-2">
                <OpenOrders
                  marketSlug={selectedMarket.slug}
                  conditionId={selectedMarket.conditionId}
                  outcomes={selectedMarket.outcomes}
                  allowedTokenIds={eventTokenIds}
                  marketMetaByTokenId={marketMetaByTokenId}
                />
              </div>
            )}

            {/* Chart · Orderbook · Trades · Top Holders · Smart Money now live
                in OutcomeDetailPanel (rendered below EventMarketsGrid in the
                main column). Keeping the sidebar focused on user-specific
                state: trading panel, positions, open orders, open interest.
                Source: see event/[slug]/components/OutcomeDetailPanel.tsx */}

            {/* Open Interest moved into OutcomeDetailPanel tab. */}

            {/* People are also trading — Kalshi only */}
            {source === 'kalshi' && eventData.seriesTicker && (
              <KalshiRelatedSeries seriesTicker={eventData.seriesTicker} />
            )}

            {/* Related markets by tag overlap — Polymarket-only. Last
                block in the primary sidebar so it sits below open
                orders + positions. Fans out gamma `/events?tag_slug=…`
                on the top 3 tags of this event and shows up to 8
                sibling events sorted by 24h vol. */}
            {source === 'polymarket' && eventData?.slug && (
              <RelatedEventsByTags eventSlug={eventData.slug} />
            )}
          </div>
        </div>
      )}

      {/* Context Sidebar Overlay (hidden by default, toggle with button) */}
      {hasContextSidebar && !hideAIContext && widgetConfig && (
        <>
          {sidebarOpen && (
            <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
          )}
          <div className={`fixed top-0 right-0 z-50 h-full w-[360px] max-w-[85vw] bg-white dark:bg-[#0f0f12] border-l border-gray-200 dark:border-[#1A1A1A] shadow-2xl transition-transform duration-300 ease-out ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-[#1A1A1A]">
              <span className="font-monument font-bold text-sm text-gray-900 dark:text-white">{t("eventSidebar.context")}</span>
              <button onClick={() => setSidebarOpen(false)} className="p-1.5 rounded-[5px] hover:bg-gray-100 dark:hover:bg-[#18181D] transition-colors">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500"><path d="M18 6L6 18"/><path d="M6 6l12 12"/></svg>
              </button>
            </div>
            <div className="overflow-y-auto h-[calc(100%-57px)] p-4 scrollbar-hide">
              <div className="space-y-4">
                {/* Match Sidebar (specific game with live score) */}
                {widgetConfig.widgetType === "match" && widgetConfig.metadata?.matchInfo && (
                  <MatchContextSidebar
                    homeTeam={widgetConfig.metadata.matchInfo.homeTeam}
                    awayTeam={widgetConfig.metadata.matchInfo.awayTeam}
                    league={widgetConfig.metadata.matchInfo.league || "soccer"}
                    matchDate={widgetConfig.metadata.matchInfo.matchDate}
                  />
                )}
                {/* Sports Sidebar (NFL, NBA, Champions League, La Liga, etc.) - for league-wide events */}
                {widgetConfig.widgetType !== "match" && widgetConfig.sportLeague && (
                  <SportsContextSidebar
                    sportLeague={widgetConfig.sportLeague}
                  />
                )}
                {/* Spotify/Music Context Sidebar */}
                {widgetConfig.widgetType === "spotify" && (
                  <SpotifyContextSidebar
                    eventTitle={eventData.title}
                    marketQuestions={eventData.markets.map(m => m.question)}
                  />
                )}
                {/* Politics/Election Context Sidebar (AI-driven) */}
                {/* Gaming/GOTY Context Sidebar */}
                {widgetConfig.widgetType === "gaming" && (
                  <GamingContextSidebar
                    eventTitle={eventData.title}
                    marketQuestions={eventData.markets.map(m => m.question)}
                  />
                )}
                {/* XTracker/Tweet Counter Sidebar */}
                {widgetConfig.widgetType === "xtracker" && widgetConfig.metadata?.xTrackerInfo && (
                  <>
                    <XTrackerContextSidebar
                      handle={widgetConfig.metadata.xTrackerInfo.handle}
                      title={eventData.title}
                      startDate={widgetConfig.metadata.xTrackerInfo.startDate}
                      endDate={widgetConfig.metadata.xTrackerInfo.endDate}
                    />
                    <XTrackerVelocityGauge
                      handle={widgetConfig.metadata.xTrackerInfo.handle}
                      title={eventData.title}
                      startDate={widgetConfig.metadata.xTrackerInfo.startDate}
                      endDate={widgetConfig.metadata.xTrackerInfo.endDate}
                    />
                    <XTrackerProgressChart
                      handle={widgetConfig.metadata.xTrackerInfo.handle}
                      title={eventData.title}
                      startDate={widgetConfig.metadata.xTrackerInfo.startDate}
                      endDate={widgetConfig.metadata.xTrackerInfo.endDate}
                    />
                  </>
                )}

                {/* AI-Powered Context Sidebar */}
                {shouldUseAI && aiAnalysis && (
                  <>
                    {aiAnalysis.geopoliticsData || aiAnalysis.contextType === "geopolitics_conflict" ? (
                      <GeopoliticsAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                      />
                    ) : aiAnalysis.category === "politics" && aiAnalysis.politicsData ? (
                      <PoliticsAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        marketQuestions={eventData.markets.map(m => m.question)}
                        markets={eventData.markets}
                      />
                    ) : aiAnalysis.category === "business" && aiAnalysis.financeData ? (
                      <FinanceAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        marketQuestions={eventData.markets.map(m => m.question)}
                        markets={eventData.markets}
                      />
                    ) : aiAnalysis.cryptoData || aiAnalysis.contextType === "crypto_market" || aiAnalysis.contextType === "crypto_price" ? (
                      <CryptoAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        markets={eventData.markets}
                      />
                    ) : aiAnalysis.awardsData || aiAnalysis.contextType === "awards_ceremony" ? (
                      <AwardsAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        markets={eventData.markets}
                      />
                    ) : aiAnalysis.weatherData || aiAnalysis.contextType === "weather_forecast" ? (
                      <WeatherAISidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        markets={eventData.markets}
                      />
                    ) : aiAnalysis.contextType !== "generic" ? (
                      <AIContextSidebar
                        analysis={aiAnalysis}
                        eventTitle={eventData.title}
                        marketQuestions={eventData.markets.map(m => m.question)}
                      />
                    ) : activeMarkets.length > 0 ? (
                      <GenericEventSidebar
                        markets={activeMarkets}
                        eventSlug={eventData.slug}
                        eventTitle={eventData.title}
                        onTrade={(params) => openTradingModal({ isOpen: true, ...params })}
                        getRealtimePrice={getLivePrice}
                        getBuyPrices={getLiveBuyPrices}
                      />
                    ) : null}
                  </>
                )}

                {/* Generic fallback when AI analysis failed or is unavailable */}
                {shouldUseAI && !aiAnalysis && !aiLoading && eventData && activeMarkets.length > 0 && (
                  <GenericEventSidebar
                    markets={activeMarkets}
                    eventSlug={eventData.slug}
                    eventTitle={eventData.title}
                    onTrade={(params) => openTradingModal({ isOpen: true, ...params })}
                    getRealtimePrice={getRealtimePrice}
                    getBuyPrices={getBuyPrices}
                  />
                )}

                {/* AI Loading State */}
                {shouldUseAI && aiLoading && (
                  <div className={`bg-gradient-to-br ${
                    widgetConfig.widgetType === "politics_ai" ? "from-purple-500/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-purple-500/30" :
                    widgetConfig.widgetType === "finance_ai" ? "from-emerald-500/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-emerald-500/30" :
                    widgetConfig.widgetType === "geopolitics_ai" ? "from-red-500/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-red-500/30" :
                    widgetConfig.widgetType === "crypto_ai" ? "from-[#F7931A]/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-[#F7931A]/30" :
                    widgetConfig.widgetType === "awards_ai" ? "from-yellow-500/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-yellow-500/30" :
                    widgetConfig.widgetType === "weather_ai" ? "from-sky-400/10 via-[#18181D] to-[#0f0f12] rounded-[5px] border border-sky-400/30" :
                    "from-gray-100 dark:from-[#18181D] via-gray-50 dark:via-[#18181D] to-white dark:to-[#0f0f12] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A]"
                  } p-4`}>
                    <div className="flex items-center gap-2 mb-4">
                      <div className={`w-5 h-5 border-2 ${
                        widgetConfig.widgetType === "politics_ai" ? "border-purple-400" :
                        widgetConfig.widgetType === "finance_ai" ? "border-emerald-400" :
                        widgetConfig.widgetType === "geopolitics_ai" ? "border-red-400" :
                        widgetConfig.widgetType === "crypto_ai" ? "border-[#F7931A]" :
                        widgetConfig.widgetType === "awards_ai" ? "border-yellow-400" :
                        widgetConfig.widgetType === "weather_ai" ? "border-sky-400" :
                        "border-gray-400 dark:border-gray-500"
                      } border-t-transparent rounded-full animate-spin`} />
                      <span className="text-gray-900 dark:text-white font-satoshi font-bold text-sm">
                        {widgetConfig.widgetType === "politics_ai" ? t("eventSidebar.aiLoading.politics") :
                         widgetConfig.widgetType === "finance_ai" ? t("eventSidebar.aiLoading.finance") :
                         widgetConfig.widgetType === "geopolitics_ai" ? t("eventSidebar.aiLoading.geopolitics") :
                         widgetConfig.widgetType === "crypto_ai" ? t("eventSidebar.aiLoading.crypto") :
                         widgetConfig.widgetType === "awards_ai" ? t("eventSidebar.aiLoading.awards") :
                         widgetConfig.widgetType === "weather_ai" ? t("eventSidebar.aiLoading.weather") :
                         t("eventSidebar.aiLoading.generic")}
                      </span>
                    </div>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                          <div className="h-12 bg-gray-200 dark:bg-[#0f0f12] rounded-[5px]" />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
}
